import React, { useEffect, useState } from 'react';
import API from '../api/axios';
import Header from '../components/Header';
import { Search, Filter, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-blue-100 text-blue-700', in_progress: 'bg-purple-100 text-purple-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-600' };
const STATUS_OPTIONS = ['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'];

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    API.get('/bookings/').then(r => setBookings(r.data)).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const { data } = await API.patch(`/bookings/${id}/`, { status: newStatus });
      setBookings(bs => bs.map(b => b.id === id ? data : b));
      toast.success('Status updated');
    } catch { toast.error('Update failed'); }
    finally { setUpdatingId(null); }
  };

  const filtered = bookings.filter(b => {
    const matchSearch = !search || b.customer_name?.toLowerCase().includes(search.toLowerCase()) || b.service_detail?.name?.toLowerCase().includes(search.toLowerCase()) || String(b.id).includes(search);
    const matchFilter = filter === 'all' || b.status === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div className="flex-1 overflow-auto">
      <Header title="Bookings" subtitle={`${bookings.length} total bookings`} />
      <div className="p-6 space-y-4">
        {/* Filters */}
        <div className="card flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search by customer, service, ID..." className="input pl-9"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', ...STATUS_OPTIONS].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all capitalize ${filter === s ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-gray-200 text-gray-600 hover:border-orange-200'}`}>
                {s === 'all' ? 'All' : s.replace('_', ' ')}
                <span className="ml-1 opacity-70">
                  ({s === 'all' ? bookings.length : bookings.filter(b => b.status === s).length})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-x-auto p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-header">ID</th>
                <th className="table-header">Customer</th>
                <th className="table-header">Service</th>
                <th className="table-header">Professional</th>
                <th className="table-header">Date & Time</th>
                <th className="table-header">Amount</th>
                <th className="table-header">Status</th>
                <th className="table-header">Payment</th>
                <th className="table-header">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {[...Array(9)].map((_, j) => <td key={j} className="table-cell"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}
                  </tr>
                ))
              ) : filtered.map(b => (
                <tr key={b.id} className="border-b border-gray-50 hover:bg-orange-50/30 transition-colors">
                  <td className="table-cell font-mono text-xs text-gray-400">#{b.id}</td>
                  <td className="table-cell">
                    <div className="font-semibold text-gray-900 text-xs">{b.customer_name}</div>
                    <div className="text-xs text-gray-400">{b.city}</div>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <img src={b.service_detail?.image} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                      <span className="text-xs font-medium text-gray-700 max-w-28 truncate">{b.service_detail?.name}</span>
                    </div>
                  </td>
                  <td className="table-cell text-xs text-gray-600">{b.provider_detail?.name || '—'}</td>
                  <td className="table-cell text-xs text-gray-500">
                    {new Date(b.scheduled_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}<br />
                    {b.scheduled_time?.slice(0, 5)}
                  </td>
                  <td className="table-cell font-bold text-gray-900">₹{b.amount}</td>
                  <td className="table-cell">
                    <span className={`badge ${STATUS_COLORS[b.status]}`}>{b.status.replace('_', ' ')}</span>
                  </td>
                  <td className="table-cell">
                    <span className={`badge ${b.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {b.payment_status}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="relative group">
                      <select
                        value={b.status}
                        onChange={e => updateStatus(b.id, e.target.value)}
                        disabled={updatingId === b.id || b.status === 'cancelled'}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 pr-6 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white cursor-pointer disabled:opacity-50">
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <div className="text-4xl mb-3">📋</div>
              <p>No bookings found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
